import { ObjectId, View } from "@novorender/api";
import { ObjectDB } from "@novorender/data-js-api";

import { SelectionResolution } from "features/render/types";

import { getParentPath } from "./objectData";

export interface SelectionTarget {
    /** The main object ID (used for setMainObject) */
    mainObjectId: ObjectId;
    /** All object IDs to highlight (includes descendants for folder objects) */
    highlightIds: ObjectId[];
}

/**
 * Resolves the selection target based on the selection resolution setting.
 *
 * For LastObject: returns the original clicked object ID only
 * For FirstObject: returns the parent of the clicked object and all its descendants
 *
 * @param objectId - The originally picked object ID
 * @param db - The object database
 * @param view - The view instance
 * @param resolution - The selection resolution setting
 * @returns The selection target with main object ID and all IDs to highlight
 */
export async function resolveSelectionTarget({
    objectId,
    db,
    view,
    resolution,
}: {
    objectId: ObjectId;
    db: ObjectDB;
    view: View;
    resolution: SelectionResolution;
}): Promise<SelectionTarget> {
    // LastObject: return the original object (current behavior)
    if (resolution === SelectionResolution.LastObject) {
        return {
            mainObjectId: objectId,
            highlightIds: [objectId],
        };
    }

    // FirstObject: select the parent of the clicked object and all its descendants
    try {
        const metadata = await (view.data ? view.data.getObjectMetaData(objectId) : db.getObjectMetdata(objectId));

        // Get the parent path
        const parentPath = getParentPath(metadata.path);

        // If no parent path (we're at root), return original object
        if (!parentPath) {
            return {
                mainObjectId: objectId,
                highlightIds: [objectId],
            };
        }

        // Find the parent object
        let parentObjectId: ObjectId | undefined;
        const highlightIds: ObjectId[] = [];

        // Search for the parent object itself
        const parentOfParentPath = getParentPath(parentPath);
        const parentSearchIter = db.search(
            {
                parentPath: parentOfParentPath,
                descentDepth: 0,
                full: true,
            },
            undefined,
        );

        for await (const obj of parentSearchIter) {
            if (obj.path === parentPath) {
                parentObjectId = obj.id;
                highlightIds.push(obj.id);
                break;
            }
        }

        // Get all descendants of the parent (children, grandchildren, etc.)
        const descendantsIter = db.search(
            {
                parentPath: parentPath,
                full: true,
            },
            undefined,
        );

        for await (const obj of descendantsIter) {
            highlightIds.push(obj.id);
            // If we didn't find the parent object, use the first descendant
            if (parentObjectId === undefined) {
                parentObjectId = obj.id;
            }
        }

        // If we found objects, return them
        if (highlightIds.length > 0 && parentObjectId !== undefined) {
            return {
                mainObjectId: parentObjectId,
                highlightIds,
            };
        }

        // Fallback: return original object if nothing found
        return {
            mainObjectId: objectId,
            highlightIds: [objectId],
        };
    } catch (error) {
        console.warn("Error resolving selection target:", error);
        return {
            mainObjectId: objectId,
            highlightIds: [objectId],
        };
    }
}
